import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/feature/FaqSection";

const contactFaq = [
  {
    question: "GRANSTARKはどんな警備会社ですか？",
    answer: "株式会社GRANSTARKは2019年創業の大阪密着の警備会社です。大阪府公安委員会認定（第62003216号）を取得しており、交通誘導・高速道路・イベント（雑踏）・施設警備の4分野に対応しています。",
  },
  {
    question: "GRANSTARKに警備を依頼するにはどうすればいいですか？",
    answer: "お電話（06-6786-9252）またはホームページのお問い合わせフォームからご連絡ください。最短即日でご対応いたします。",
  },
  {
    question: "対応エリアはどこですか？",
    answer: "主に大阪府内全域に対応しています。",
  },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[0-9\-+\(\)\s]{10,15}$/;

function sanitize(input: string): string {
  return input
    .replace(/<[^>]*>/g, "")
    .replace(/[<>"'&]/g, "");
}

function generateCsrfToken(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

function getCsrfToken(): string {
  let token = sessionStorage.getItem("csrf_token");
  if (!token) {
    token = generateCsrfToken();
    sessionStorage.setItem("csrf_token", token);
  }
  return token;
}

function checkRateLimit(): { allowed: boolean; remaining: number } {
  const key = "form_submit_log";
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;
  const maxAttempts = 5;

  const raw = localStorage.getItem(key);
  let timestamps: number[] = [];
  if (raw) {
    try {
      timestamps = JSON.parse(raw);
    } catch {
      timestamps = [];
    }
  }

  const validTimestamps = timestamps.filter((t) => now - t < oneHour);
  const allowed = validTimestamps.length < maxAttempts;
  const remaining = Math.max(0, maxAttempts - validTimestamps.length);

  return { allowed, remaining };
}

function recordSubmission(): void {
  const key = "form_submit_log";
  const raw = localStorage.getItem(key);
  let timestamps: number[] = [];
  if (raw) {
    try {
      timestamps = JSON.parse(raw);
    } catch {
      timestamps = [];
    }
  }
  timestamps.push(Date.now());
  localStorage.setItem(key, JSON.stringify(timestamps));
}

export default function Contact() {
  useSeo({
    title: "お問い合わせ｜株式会社GRANSTARK｜大阪の警備会社",
    description:
      "警備のご依頼・お見積もりはGRANSTARKへ。交通誘導・イベント警備・高速道路警備・施設警備について、お電話またはお問い合わせフォームよりお気軽にご相談ください。",
    ogUrl: "https://www.gs-granstark.com/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://www.gs-granstark.com/contact#webpage",
        url: "https://www.gs-granstark.com/contact",
        name: "お問い合わせ｜株式会社GRANSTARK｜大阪の警備会社",
        description:
          "警備のご依頼・お見積もりはGRANSTARKへ。交通誘導・イベント警備・高速道路警備・施設警備について、お電話またはお問い合わせフォームよりお気軽にご相談ください。",
        isPartOf: { "@id": "https://www.gs-granstark.com/#website" },
        publisher: { "@id": "https://www.gs-granstark.com/#business" },
        inLanguage: "ja",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: contactFaq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  });

  const navigate = useNavigate();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    type: "",
    message: "",
    privacy: false,
    honeypot: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>();
  const csrfToken = getCsrfToken();
  const email = ['contact', 'granstark.co.jp'].join('@');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    if (target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [target.name]: (target as HTMLInputElement).checked,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [target.name]: sanitize(target.value) }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.type) {
      errors.type = "お問い合わせ種別を選択してください";
    }
    if (!formData.name.trim()) {
      errors.name = "お名前を入力してください";
    } else if (formData.name.trim().length > 50) {
      errors.name = "お名前は50文字以内で入力してください";
    }
    if (!formData.phone.trim()) {
      errors.phone = "電話番号を入力してください";
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = "電話番号の形式が正しくありません";
    }
    if (!formData.email.trim()) {
      errors.email = "メールアドレスを入力してください";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "メールアドレスの形式が正しくありません";
    }
    if (!formData.message.trim()) {
      errors.message = "お問い合わせ内容を入力してください";
    } else if (formData.message.trim().length > 500) {
      errors.message = "お問い合わせ内容は500文字以内で入力してください";
    }
    if (!formData.privacy) {
      errors.privacy = "プライバシーポリシーに同意してください";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    if (!validateForm()) {
      return;
    }

    // Honeypot check
    if (formData.honeypot && formData.honeypot.trim().length > 0) {
      setFormErrors({ global: "不正なリクエストです。" });
      return;
    }

    // Rate limit check
    const rateLimit = checkRateLimit();
    if (!rateLimit.allowed) {
      setFormErrors({
        global: "送信回数の上限に達しました。1時間後に再度お試しください。",
      });
      return;
    }

    // CSRF token check
    const storedCsrf = sessionStorage.getItem("csrf_token");
    if (!storedCsrf || storedCsrf !== csrfToken) {
      setFormErrors({ global: "セッションが無効です。ページを更新してください。" });
      return;
    }

    setSubmitting(true);

    try {
      const form = formRef.current;
      const formDataObj = new FormData(form);

      // Append security fields
      formDataObj.append("csrf_token", csrfToken);

      const response = await fetch("https://formspree.io/f/xdajeqea", {
        method: "POST",
        body: formDataObj,
        redirect: "manual",
      });

      if (response.ok || response.type === "opaqueredirect") {
        recordSubmission();
        // PRG pattern: redirect to thanks page
        navigate("/thanks", { replace: true });
      } else {
        setFormErrors({ global: "送信中にエラーが発生しました。もう一度お試しください。" });
      }
    } catch {
      setFormErrors({ global: "送信中にエラーが発生しました。もう一度お試しください。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{
            backgroundImage:
              "url('/images/kv-main.jpg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/60 to-navy/80 pointer-events-none" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-3">CONTACT</span>
            <h1 className="text-4xl md:text-5xl font-black text-white">お問い合わせ / contact</h1>
            <p className="text-gray-300 mt-4 text-lg">
              警備のご依頼・見積もり・採用に関するご質問など、お気軽にご連絡ください
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="bg-white py-16 md:py-24">
        <div ref={ref} className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* 左側：お問い合わせ先情報 */}
            <div>
              <div className="mb-10">
                <span className="text-gold text-sm tracking-[3px] font-bold block mb-2">CONTACT INFO</span>
                <h2 className="text-2xl md:text-3xl font-bold text-navy">お問い合わせ先</h2>
              </div>

              <div className="bg-gold/10 rounded-2xl p-6 md:p-8 mb-8">
                <p className="text-sm text-navy font-bold mb-2">電話でのお問い合わせ</p>
                <a
                  href="tel:08045635272"
                  className="text-3xl md:text-4xl font-black text-gold hover:underline block mb-2"
                >
                  TEL: 080-4563-5272
                </a>
                <p className="text-gray-600 text-sm">受付時間：平日 9:00～18:00</p>
              </div>

              <div className="space-y-4 text-sm text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-navy"></i>
                  </span>
                  <div>
                    <p className="font-bold text-navy">メール</p>
                    <a href={'mailto:' + email} className="text-gold hover:underline">
                      {email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-navy"></i>
                  </span>
                  <div>
                    <p className="font-bold text-navy">所在地</p>
                    <p>〒556-0003<br />大阪府大阪市浪速区難波中3-9-3<br />南朝日ビル804号</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-navy"></i>
                  </span>
                  <div>
                    <p className="font-bold text-navy">会社名</p>
                    <p>株式会社GRANSTARK</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 右側：フォーム */}
            <div>
              <div className="mb-8">
                <span className="text-gold text-sm tracking-[3px] font-bold block mb-2">INQUIRY</span>
                <h2 className="text-2xl md:text-3xl font-bold text-navy">お問い合わせ・ご応募</h2>
              </div>

              {formErrors?.global && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formErrors.global}
                </div>
              )}

              <form
                ref={formRef}
                data-readdy-form
                id="contact-form"
                action="https://formspree.io/f/xdajeqea"
                method="POST"
                onSubmit={handleSubmit}
                className={`space-y-5 transition-all duration-700 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
              >
                {/* Honeypot field */}
                <div className="absolute -top-[9999px] left-0 w-px h-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="website">ウェブサイト（入力しないでください）</label>
                  <input
                    type="text"
                    id="website"
                    name="honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.honeypot}
                    onChange={handleChange}
                  />
                </div>

                {/* CSRF token hidden input */}
                <input type="hidden" name="csrf_token" value={csrfToken} />

                {/* お問い合わせ種別（必須） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-3">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="警備のご依頼・お見積もり"
                        required
                        checked={formData.type === "警備のご依頼・お見積もり"}
                        onChange={handleChange}
                        className="accent-gold w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">警備のご依頼・お見積もり</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="採用・応募について"
                        required
                        checked={formData.type === "採用・応募について"}
                        onChange={handleChange}
                        className="accent-gold w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">採用・応募について</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="その他"
                        required
                        checked={formData.type === "その他"}
                        onChange={handleChange}
                        className="accent-gold w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">その他</span>
                    </label>
                  </div>
                  {formErrors?.type && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.type}</p>
                  )}
                </div>

                {/* お名前（必須） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="山田 太郎"
                  />
                  {formErrors?.name && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* 会社名（任意） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-2">会社名（任意）</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="株式会社○○"
                  />
                </div>

                {/* 電話番号（必須） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="090-1234-5678"
                  />
                  {formErrors?.phone && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
                  )}
                </div>

                {/* メールアドレス（必須） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors"
                    placeholder="example@email.com"
                  />
                  {formErrors?.email && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                  )}
                </div>

                {/* お問い合わせ内容（必須） */}
                <div>
                  <label className="block text-sm font-bold text-navy mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-gold transition-colors resize-none"
                    placeholder="お問い合わせ内容をご記入ください"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {formData.message.length} / 500文字
                  </p>
                  {formErrors?.message && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
                  )}
                </div>

                {/* プライバシーポリシー同意チェックボックス（必須） */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy"
                      required
                      checked={formData.privacy}
                      onChange={handleChange}
                      className="accent-gold w-4 h-4 mt-0.5 shrink-0"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed">
                      <a href="#" className="text-gold underline hover:no-underline">
                        プライバシーポリシー
                      </a>
                      に同意します <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {formErrors?.privacy && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.privacy}</p>
                  )}
                </div>

                {/* 送信ボタン */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gold text-navy font-bold text-base py-4 rounded-full transition-all duration-300 hover:bg-gold-light hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "送信中..." : "送信する"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <FaqSection items={contactFaq} bgColor="bg-gray-50" />
    </div>
  );
}
